class CustomTextPreview extends HTMLElement {
  constructor() {
    super();

    // Other config
    this.customSkuCost = 8;
    this.minCharacters = 1;

    // Store modal orchestrator handlers
    this.customizationModalCleanup = null;
    this.customizationModalOnComplete = null;
    this.confirmationModalCleanup = null;
    this.confirmationModalOnComplete = null;
  }

  connectedCallback() {
    const customSkuVariantId = this.dataset.customSkuVariantId;
    this.useCustomSku = !!customSkuVariantId;
    // Non-mandatory (optional) when product has customizable tag — shows confirmation modal before customizing
    this.customizationIsMandatory = this.dataset.customizable !== 'true';

    this.mainForm = document.querySelector('form[id^="product-form-template"]');
    if (!this.mainForm) {
      console.error("main form not found");
      return;
    }
    /**
     * @type {HTMLInputElement | null}
     */
    const selectedVariantElement =
      this.mainForm.querySelector('input[name="id"]');
    if (!selectedVariantElement) {
      console.error("selected variant element not found");
      return;
    }
    this.selectedVariantId = selectedVariantElement.value;

    // Initialize _customized property on the main form so it's always present
    let customizedInput = this.mainForm.querySelector("input[name='properties[_customized]']");
    if (!customizedInput) {
      customizedInput = document.createElement('input');
      customizedInput.type = 'hidden';
      customizedInput.name = 'properties[_customized]';
      this.mainForm.appendChild(customizedInput);
    }
    customizedInput.value = '';

    /**
     * @type {HTMLElement | null}
     */
    const variantSelectsEl = document.querySelector("variant-selects");
    const productEl = variantSelectsEl
      ? variantSelectsEl.querySelector("script[id='product-json']")
      : null;
    if (productEl) {
      try {
        this.product = JSON.parse(productEl.textContent);
      } catch (error) {
        console.error("Error parsing product JSON:", error);
      }
    }
    if (!this.product) {
      console.error("custom-sku: product JSON not found, component disabled");
      return;
    }
    this.selectedVariant = this.product.variants.find(
      /** @param {Variant} variant */ (variant) =>
        variant.id.toString() === this.selectedVariantId
    );

    // ----- Scoped (inside <custom-sku>) -----
    /**
     * @type {HTMLInputElement | null}
     */
    this.hiddenVariantToCustomizeInput = this.querySelector(
      "input[name='properties[_variant-to-customize]']"
    );
    if (this.hiddenVariantToCustomizeInput) {
      this.hiddenVariantToCustomizeInput.value = this.selectedVariantId;
    }

    this.form = this.querySelector("form");
    this.setAddCustomSkuToCart(false);
    this.customSkuCancelBtn = this.querySelector(
      "#cancel-custom-text-modal-btn"
    );
    /**
     * @type {HTMLButtonElement | null}
     */
    this.customSkuAddBtn = this.querySelector("#add-to-cart-custom-text-btn");
    /**
     * @type {HTMLDivElement | null}
     */
    this.customizationModal = this.querySelector("#product-modal");
    /**
     * @type {HTMLDivElement | null}
     */
    this.overlay = this.querySelector(".product-modal__overlay");
    /**
     * @type {HTMLButtonElement | null}
     */
    this.closeBtn = this.querySelector(".product-modal__close");
    /**
     * @type {HTMLImageElement | null}
     */
    this.mainImage = this.querySelector(".modal-gallery-main-image");
    /**
     * @type {HTMLImageElement | null}
     */
    this.variantImageEl = this.querySelector(".variant-image");
    /**
     * @type {HTMLDivElement | null}
     */
    this.variantTitleEl = this.querySelector(".variant");
    /**
     * @type {HTMLButtonElement | null}
     */
    this.openModalBtn = this.querySelector(".product__custom-sku");
    this.disclaimerToggle = this.querySelector(".disclaimer-toggle");
    /**
     * @type {HTMLDivElement | null}
     */
    this.disclaimerContent = this.querySelector(".disclaimer-content");
    /**
     * @type {HTMLInputElement | null}
     */
    this.customTextInput = this.querySelector("#custom-text-input");
    this.confirmationModal = this.querySelector("#confirmation-modal");
    this.confirmationOverlay = this.confirmationModal
      ? this.confirmationModal.querySelector(".product-modal__overlay")
      : null;
    /**
     * @type {HTMLButtonElement | null}
     */
    this.yesCustomizeBtn = this.querySelector("#yes-customize-btn");
    this.noAddToCartBtn = this.querySelector("#no-add-to-cart-btn");

    // Outside of <custom-sku>
    this.productInfoWrapper = document.querySelector(".product__info-wrapper");
    /**
     * @type {HTMLButtonElement | null}
     */
    this.mainAddToCartBtn = this.productInfoWrapper
      ? this.productInfoWrapper.querySelector(".product-form__submit")
      : null;

    // Basic guards
    if (!this.customizationModal) {
      console.error(
        "[CustomSku] Customization modal not found inside <custom-sku>."
      );
      return;
    }
    if (!this.openModalBtn) {
      console.error("[CustomSku] #open-custom-sku-modal-btn not found.");
      return;
    }

    this.customizationPreviewUrl = this.dataset.customizationPreviewUrl;

    // Set up events
    this.setupEventListeners();

    // a11y: focus traps for both modals
    this._previouslyFocused = null;
    if (typeof focusTrap !== 'undefined') {
      this._customizationFocusTrap = focusTrap.createFocusTrap(this.customizationModal, {
        initialFocus: () => this.customTextInput || this.customizationModal,
        escapeDeactivates: true,
        allowOutsideClick: true,
        onDeactivate: () => {
          if (this.customizationModal.classList.contains('modal-fade-visible')) {
            this.closeCustomizationModal();
          }
          if (this._previouslyFocused?.focus) this._previouslyFocused.focus();
        },
      });
      if (this.confirmationModal) {
        this._confirmationFocusTrap = focusTrap.createFocusTrap(this.confirmationModal, {
          initialFocus: () => this.yesCustomizeBtn || this.confirmationModal,
          escapeDeactivates: true,
          allowOutsideClick: true,
          onDeactivate: () => {
            if (this.confirmationModal.classList.contains('modal-fade-visible')) {
              this.closeConfirmationModal();
            }
            if (this._previouslyFocused?.focus) this._previouslyFocused.focus();
          },
        });
      }
    }

    // Register button with orchestrator
    if (window.modalOrchestrator && this.mainAddToCartBtn) {
      window.modalOrchestrator.registerButton(this.mainAddToCartBtn, this);
      console.log("[CustomTextPreview] Registered button with orchestrator");
    }

    if (window.modalOrchestrator && this.openModalBtn) {
      window.modalOrchestrator.registerButton(this.openModalBtn, this, {
        addToCartButton: this.mainAddToCartBtn,
        userWantsToCustomize: true,
      });
      console.log("[CustomTextPreview] Registered button with orchestrator");
    }

    // Disclaimer toggle (scoped)
    if (this.disclaimerToggle && this.disclaimerContent) {
      this.disclaimerToggle.addEventListener("click", () => {
        if (!this.disclaimerToggle || !this.disclaimerContent) return;
        const expanded =
          this.disclaimerToggle.getAttribute("aria-expanded") === "true";
        this.disclaimerToggle.setAttribute(
          "aria-expanded",
          (!expanded).toString()
        );
        this.disclaimerContent.hidden = expanded;
      });
    }

    // Init state
    this.variantUpdateHandler(this.selectedVariantId);

    this.registerConfirmationModalWithOrchestrator();
    this.registerCustomizationModalWithOrchestrator();

    // Listen for product form reset to reset customization modal
    messenger.listen(messenger.topics.RESET_PRODUCT_FORM, () => {
      this.resetCustomizationModal();
    });

    messenger.listen(messenger.topics.VARIANT_CHANGE, (data) => {
      this.variantUpdateHandler(data.id);
    });
  }

  registerConfirmationModalWithOrchestrator() {
    console.log("[CustomTextPreview] Registering confirmation modal");
    if (!window.modalOrchestrator) {
      console.warn("[CustomTextPreview] modalOrchestrator not available");
      return;
    }

    console.log("[CustomTextPreview] modalOrchestrator found, registering...");
    window.modalOrchestrator.register({
      name: "custom-text-confirmation",
      priority: 40, // After holiday shipping

      shouldShow: (context) => {
        console.log("[CustomTextPreview] shouldShow called");
        if (context.userWantsToCustomize || this.customizationIsMandatory) {
          return false;
        }
        return true;
      },

      show: (context, onComplete) => {
        console.log(
          "[CustomTextPreview] show called - opening confirmation modal"
        );
        this.openConfirmationModal();

        const handleYes = () => {
          console.log("User chose to customize");
          cleanup();
          this.confirmationModalCleanup = null;
          this.confirmationModalOnComplete = null;
          this.closeConfirmationModal();
          this.openCustomizationModal();
          context.userWantsToCustomize = true;
          onComplete(true);
        };

        const handleNo = () => {
          console.log("User chose to add without customization");
          cleanup();
          this.confirmationModalCleanup = null;
          this.confirmationModalOnComplete = null;
          this.closeConfirmationModal();
          context.userWantsToCustomize = false;
          onComplete(true);
        };

        const cleanup = () => {
          if (this.noAddToCartBtn) {
            this.noAddToCartBtn.removeEventListener("click", handleNo);
          }
          if (this.yesCustomizeBtn) {
            this.yesCustomizeBtn.removeEventListener("click", handleYes);
          }
        };

        // Store so closeConfirmationModal() can cancel the orchestrator flow
        // when the user dismisses via overlay or focus trap escape.
        this.confirmationModalCleanup = cleanup;
        this.confirmationModalOnComplete = onComplete;

        if (this.noAddToCartBtn) {
          this.noAddToCartBtn.addEventListener("click", handleNo);
        }
        if (this.yesCustomizeBtn) {
          this.yesCustomizeBtn.addEventListener("click", handleYes);
        }
      },
    });
  }

  registerCustomizationModalWithOrchestrator() {
    if (!window.modalOrchestrator) {
      console.warn("[CustomText] modalOrchestrator not available");
      return;
    }

    window.modalOrchestrator.register({
      name: "custom-text-customization",
      priority: 50, // After holiday shipping

      shouldShow: (context) => {
        if (context.userWantsToCustomize || this.customizationIsMandatory) {
          return true;
        }
        return false;
      },

      show: (context, onComplete) => {
        this.openCustomizationModal();

        const handleAddToCart = () => {
          if (this.customSkuAddBtn) this.customSkuAddBtn.disabled = true;

          // Set _customized and Your Text on the main form so product-form.js includes them
          const text = this.customTextInput ? this.customTextInput.value : '';
          const customizedInput = this.mainForm.querySelector("input[name='properties[_customized]']");
          if (customizedInput) customizedInput.value = text;

          let yourTextInput = this.mainForm.querySelector("input[name='properties[Your Text]']");
          if (!yourTextInput) {
            yourTextInput = document.createElement('input');
            yourTextInput.type = 'hidden';
            yourTextInput.name = 'properties[Your Text]';
            this.mainForm.appendChild(yourTextInput);
          }
          yourTextInput.value = text;

          // Hide modal visually — product-form.js reads the custom SKU form
          // (add-to-cart is still "true") and bundles both items in one /cart/add.js call.
          if (this.customizationModal) {
            this.customizationModal.classList.remove("modal-fade-visible");
            this.customizationModal.classList.add("modal-fade-hidden");
          }
          cleanup();
          this.customizationModalCleanup = null;
          this.customizationModalOnComplete = null;
          // Override ships-by date for customized items
          const shipsByInput = this.mainForm.querySelector("input[id='ships-by-input']");
          if (shipsByInput) shipsByInput.value = '10businessdays';

          // Flip to true so product-form.js includes the custom SKU in its items array.
          // resetCustomizationModal() sets it back to false after 200ms.
          this.setAddCustomSkuToCart(true);
          onComplete(true); // product-form.js handles the single bundled cart add
          // Reset after the orchestrator's setTimeout(50ms) has fired and
          // product-form.js has read both form values.
          setTimeout(() => this.resetCustomizationModal(), 200);
        };

        const handleCancel = () => {
          this.closeCustomizationModal();
          cleanup();
          this.customizationModalCleanup = null;
          this.customizationModalOnComplete = null;
          onComplete(false); // Stop chain, user will add from customization modal
        };

        const cleanup = () => {
          if (this.customSkuAddBtn) {
            this.customSkuAddBtn.removeEventListener("click", handleAddToCart);
          }
          if (this.customSkuCancelBtn) {
            this.customSkuCancelBtn.removeEventListener("click", handleCancel);
          }
        };

        // Store cleanup and onComplete for use when closing via overlay/close button
        this.customizationModalCleanup = cleanup;
        this.customizationModalOnComplete = onComplete;

        if (this.customSkuAddBtn) {
          this.customSkuAddBtn.addEventListener("click", handleAddToCart);
        }
        if (this.customSkuCancelBtn) {
          this.customSkuCancelBtn.addEventListener("click", handleCancel);
        }
      },
    });
  }

  /**
   *
   * @param {string | number} variantId
   */
  async variantUpdateHandler(variantId) {
    if (!variantId) {
      console.error("Invalid variantId");
      return;
    }
    this.selectedVariantId = variantId;
    if (this.hiddenVariantToCustomizeInput) {
      this.hiddenVariantToCustomizeInput.value = variantId;
    }
    const hasCustomConfig = await this.updateImage(variantId.toString(), "");
    if (!hasCustomConfig) {
      // For customizable products, always show the button even without a preview
      if (this.dataset.customizable !== 'true') {
        this.hideOpenModalButton();
      }
      return;
    }
    if (!this.product) return;
    this.selectedVariant = this.product.variants.find(
      /** @param {Variant} variant */ (variant) =>
        variant.id.toString() === variantId.toString()
    );
    this.updateVariantHeader();
    this.updateAddToCartButtonState(
      this.customTextInput ? this.customTextInput.value : ""
    );
    if (this.customizationIsMandatory) {
      this.hideOpenModalButton();
      return;
    }
    this.showOpenModalButton();
  }

  /**
   *
   * @param {string} text
   * @returns
   */
  updateTextInputField(text) {
    // When useCustomSku is active, the text lives on the custom SKU form and is submitted via fetch
    if (this.useCustomSku) return;
    if (!this.mainForm || !this.customTextInput) {
      console.log("Main form or custom text input not found");
      return;
    }
    /**
     * @type {HTMLInputElement | null}
     */
    let customTextInputField = this.mainForm.querySelector(
      "input[name='properties[_your-text]']"
    );
    if (!customTextInputField) {
      const customTextProperty = document.createElement("input");
      customTextProperty.type = "hidden";
      customTextProperty.name = "properties[_your-text]";
      customTextProperty.value = this.customTextInput.value;

      this.mainForm.appendChild(customTextProperty);
    } else {
      customTextInputField.value = text;
    }
  }

  // ---------- Helpers ----------

  setupEventListeners() {
    if (this.overlay)
      this.overlay.addEventListener("click", () => {
        this.closeCustomizationModal();
      });
    if (this.closeBtn)
      this.closeBtn.addEventListener("click", () => {
        this.closeCustomizationModal();
      });
    if (this.confirmationOverlay) {
      this.confirmationOverlay.addEventListener("click", () => {
        this.closeConfirmationModal();
      });
    }
    // Custom text validation
    if (this.customTextInput) {
      this.customTextInput.addEventListener("input", (e) => {
        this.validateCustomTextInput(e);

        clearTimeout(this._customTextDebounce);
        this._customTextDebounce = setTimeout(() => {
          this.updateTextInputField(e.target.value);
          this.updateImage(this.selectedVariantId, e.target.value);
        }, 400);
      });

      this.customTextInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && this.customSkuAddBtn && !this.customSkuAddBtn.disabled) {
          e.preventDefault();
          this.customSkuAddBtn.click();
        }
      });
    }
  }

  // ---------- UI updates ----------

  /**
   *
   * @param {string} variantId
   * @param {string} text
   * @returns {Promise<boolean>}
   */
  async updateImage(variantId, text) {
    const url = `${this.customizationPreviewUrl}?variantId=gid://shopify/ProductVariant/${variantId}&text=${text}`;

    // Show loading state on the parent container
    if (this.mainImage && this.mainImage.parentElement) {
      this.mainImage.parentElement.classList.add("loading");
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error("Failed to fetch image");
        if (this.mainImage && this.mainImage.parentElement) {
          this.mainImage.parentElement.classList.remove("loading");
        }
        return false;
      }
      const blob = await response.blob();
      if (this.mainImage) {
        this.mainImage.src = URL.createObjectURL(blob);
        // Remove loading state after image loads
        this.mainImage.onload = () => {
          if (this.mainImage && this.mainImage.parentElement) {
            this.mainImage.parentElement.classList.remove("loading");
          }
        };
        return true;
      }
    } catch (error) {
      console.error(error);
      if (this.mainImage && this.mainImage.parentElement) {
        this.mainImage.parentElement.classList.remove("loading");
      }
      return false;
    }
    return false;
  }

  updateVariantHeader() {
    if (!this.variantTitleEl) return;
    /**
     * @type {HTMLElement | null}
     */
    const variantHeadingEl = this.querySelector(".variant");
    if (!variantHeadingEl) return;
    variantHeadingEl.innerText = this.selectedVariant.title;
  }

  hideOpenModalButton() {
    this.openModalBtn.style.display = "none";
  }
  showOpenModalButton() {
    this.openModalBtn.style.display = "block";
  }

  // ---------- Customization Modal ----------

  openCustomizationModal() {
    this.updateVariantHeader();
    if (!this.customizationModal) return;
    this._previouslyFocused = document.activeElement;
    this.customizationModal.classList.remove("modal-fade-hidden");
    this.customizationModal.classList.add("modal-fade-visible");
    // Defer focus until after the browser has rendered the modal visible
    requestAnimationFrame(() => {
      if (this._customizationFocusTrap) {
        this._customizationFocusTrap.activate();
      } else if (this.customTextInput) {
        this.customTextInput.focus();
      }
    });
  }

  closeCustomizationModal() {
    if (!this.customizationModal) return;
    this.customizationModal.classList.remove("modal-fade-visible");
    this.customizationModal.classList.add("modal-fade-hidden");
    if (this._customizationFocusTrap) this._customizationFocusTrap.deactivate();
    this.resetCustomizationModal();

    // If we have stored handlers from modal orchestrator, call them
    // This happens when closing via overlay or close button
    if (this.customizationModalCleanup && this.customizationModalOnComplete) {
      this.customizationModalCleanup();
      this.customizationModalOnComplete(false); // User cancelled
      this.customizationModalCleanup = null;
      this.customizationModalOnComplete = null;
    }
  }

  // ---------- Confirmation modal ----------

  openConfirmationModal() {
    if (!this.confirmationModal) return;
    this._previouslyFocused = document.activeElement;
    this.confirmationModal.classList.remove("modal-fade-hidden");
    this.confirmationModal.classList.add("modal-fade-visible");
    if (this._confirmationFocusTrap) this._confirmationFocusTrap.activate();
  }

  closeConfirmationModal() {
    if (!this.confirmationModal) return;
    this.confirmationModal.classList.remove("modal-fade-visible");
    this.confirmationModal.classList.add("modal-fade-hidden");
    if (this._confirmationFocusTrap) this._confirmationFocusTrap.deactivate();

    if (this.confirmationModalCleanup && this.confirmationModalOnComplete) {
      this.confirmationModalCleanup();
      this.confirmationModalOnComplete(false); // cancel orchestrator flow
      this.confirmationModalCleanup = null;
      this.confirmationModalOnComplete = null;
    }
  }

  // ---------- Cart helpers ----------

  /**
   * @param {boolean} addToCart
   */
  setAddCustomSkuToCart(addToCart) {
    if (!this.form) return;
    /**
     * @type {HTMLInputElement | null}
     */
    const addToCartInput = this.form.querySelector("input[name='add-to-cart']");
    if (!addToCartInput) return;
    if (addToCartInput) addToCartInput.value = String(addToCart);
  }

  // ---------- Input validation ----------

  validateCustomTextInput(event) {
    /**
     * @type {HTMLInputElement | null}
     */
    const input = event.target;
    if (!input) return;
    let value = input.value || "";
    value = value
      .replace(/[^A-Z\s]/gi, "") // Remove all non-alphabetic characters
      .toUpperCase()
      .substring(0, 20);

    input.value = value;
    this.updateAddToCartButtonState(value);
  }

  /**
   * @param {string} value
   */
  updateAddToCartButtonState(value) {
    if (!this.customSkuAddBtn) return;
    const isValid = (value || "").length >= this.minCharacters;
    this.customSkuAddBtn.disabled = !isValid;
    this.customSkuAddBtn.classList.toggle("button--disabled", !isValid);
    this.customSkuAddBtn.style.opacity = isValid ? "1" : "0.6";
    this.customSkuAddBtn.style.cursor = isValid ? "pointer" : "not-allowed";
  }

  // ---------- Custom SKU submission ----------

  async submitCustomSkuForm() {
    if (!this.form) return;
    const formData = new FormData(this.form);
    formData.delete('add-to-cart');
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        console.error('[CustomTextPreview] Failed to add custom SKU to cart');
      }
    } catch (error) {
      console.error('[CustomTextPreview] Error adding custom SKU:', error);
    }
  }

  // ---------- Reset ----------

  resetCustomizationModal() {
    if (!this.customizationModal) return;
    if (this.customTextInput) this.customTextInput.value = "";

    // Reset all custom-text input fields
    const customTextWrapper = document.querySelector("custom-text-wrapper");
    if (customTextWrapper) {
      const customTextFields =
        customTextWrapper.querySelectorAll("custom-text");
      customTextFields.forEach((field) => {
        const input =
          field.querySelector("input") || field.querySelector("textarea");
        if (input) {
          input.value = "";
          // Trigger input event to update validation state
          input.dispatchEvent(new Event("input", { bubbles: true }));
        }
      });
    }

    const customizedInput = this.mainForm
      ? this.mainForm.querySelector("input[name='properties[_customized]']")
      : null;
    if (customizedInput) customizedInput.value = '';

    const yourTextInput = this.mainForm
      ? this.mainForm.querySelector("input[name='properties[Your Text]']")
      : null;
    if (yourTextInput) yourTextInput.value = '';

    this.setAddCustomSkuToCart(false);
    this.updateAddToCartButtonState("");
    this.updateImage(this.selectedVariantId, "");
  }
}

customElements.define("custom-text-preview", CustomTextPreview);
